import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Writing and editing → Organize cases.
// Fixture: `organizeCases` (Valley Mart open, one existing "Burglary" category,
// a 30 day Trash window). The scene creates a category, files the open case
// into it, stars it, opens a category's menu, then trashes the domestic
// disturbance case from the list and restores it from Trash.
const REVIEW_CASE_ID = "c0000000-0000-4000-8000-000000000201";

const tagButton = `${sel("caseCategoryTag")} button`;
const tagMenu = sel("categoryTagMenu");
const dialog = sel("categoryDialog");
const deleteDialog = sel("categoryDeleteDialog");
const listItem = (n: number) => `${sel("caseListItem")}:nth-child(${n})`;

const scene: Scene = {
  id: "categories-favorites-and-trash",
  title: "Organize cases",
  startRoute: `/cases/${REVIEW_CASE_ID}`,
  fixture: "organizeCases",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "When your case list gets long, categories let you group it the way you think about your work. They are personal to you, so nobody else sees your list.",
      actions: [
        { type: "waitFor", target: sel("caseCategories") },
        { type: "waitFor", target: sel("favoriteStar") },
        { type: "wait", ms: 4300 },
        { type: "spotlight", target: sel("caseCategories") },
        { type: "wait", ms: 3200 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "create",
      narration:
        "In the sidebar, click the plus next to Categories. In the New Category dialog, type a name and click Create.",
      actions: [
        { type: "wait", ms: 1500 },
        { type: "click", target: sel("newCategoryButton") },
        { type: "waitFor", target: `${dialog} input` },
        { type: "wait", ms: 1500 },
        { type: "type", target: `${dialog} input`, text: "Retail Theft", cps: 9 },
        { type: "wait", ms: 200 },
        { type: "click", target: `${dialog} .modal-action button:last-child` },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "tag",
      narration:
        "To file a case, open it and click the tag icon in the toolbar, then tick the categories. One case can sit in several. You can also drag a case from the list onto a category.",
      actions: [
        { type: "wait", ms: 2300 },
        { type: "click", target: tagButton },
        { type: "waitFor", target: tagMenu },
        { type: "spotlight", target: tagMenu },
        { type: "wait", ms: 1500 },
        // The new category only appears once the list refetch lands (a seek replays instantly).
        { type: "waitFor", target: `${tagMenu} li:nth-child(3)` },
        { type: "click", target: `${tagMenu} li:nth-child(2) button` },
        { type: "wait", ms: 2200 },
        { type: "spotlight", target: null },
        { type: "click", target: tagButton },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "favorite",
      narration: "Click the star in the editor toolbar to add the case to Favorites. It is pinned under Favorites in the sidebar.",
      actions: [
        { type: "wait", ms: 800 },
        { type: "click", target: sel("favoriteStar") },
        { type: "wait", ms: 2100 },
        { type: "click", target: sel("favoritesCategory") },
        { type: "wait", ms: 1400 },
        { type: "click", target: `${sel("casesNav")} button` },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "manage",
      narration:
        "To change a category, hover it and click the three dots for Rename or Delete. Deleting a category never deletes the cases in it.",
      actions: [
        { type: "wait", ms: 700 },
        { type: "moveTo", target: sel("categoryRow") },
        { type: "wait", ms: 600 },
        { type: "click", target: sel("categoryMenuButton") },
        { type: "waitFor", target: sel("categoryMenu") },
        { type: "spotlight", target: sel("categoryMenu") },
        { type: "wait", ms: 1500 },
        { type: "click", target: `${sel("categoryMenu")} li:nth-child(2) button` },
        { type: "waitFor", target: `${deleteDialog} .modal-box` },
        { type: "spotlight", target: `${deleteDialog} .modal-box` },
        { type: "wait", ms: 2000 },
        { type: "click", target: `${deleteDialog} .modal-action button:first-child` },
        { type: "spotlight", target: null },
        { type: "wait", ms: 600 },
      ],
    },
    {
      id: "trash",
      narration:
        "To delete a case, select it in the list and click the trash icon, or drag it onto Trash in the sidebar. Then click Trash to see what you have deleted.",
      actions: [
        { type: "wait", ms: 500 },
        { type: "click", target: listItem(2) },
        { type: "wait", ms: 1000 },
        { type: "click", target: sel("deleteCase") },
        { type: "wait", ms: 2800 },
        // The list shrinks to two once the delete lands (a seek replays instantly).
        { type: "waitFor", target: `${listItem(2)}:last-child` },
        { type: "click", target: `${sel("trashNav")} button` },
        { type: "waitFor", target: sel("trashBanner") },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "restore",
      narration:
        "The banner says how long cases stay in Trash. That number comes from your department's Data Retention policy. To bring a case back, open it and click Restore, where Export normally is.",
      actions: [
        { type: "spotlight", target: sel("trashBanner") },
        { type: "wait", ms: 6600 },
        { type: "spotlight", target: null },
        { type: "click", target: sel("caseListItem") },
        { type: "waitFor", target: sel("editorRestore") },
        { type: "spotlight", target: sel("editorRestore") },
        { type: "wait", ms: 1900 },
        { type: "click", target: sel("editorRestore") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1000 },
      ],
    },
    {
      id: "outro",
      narration:
        "Restoring puts a case back exactly as it was, categories included. Trashed cases do not show in categories or Favorites, and search skips them.",
      actions: [{ type: "wait", ms: 4000 }],
    },
  ],
};

export default scene;
